import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import type { ActivityEvent } from './activityFeedApi';
import { resolveActivityCtaPath } from '../../utils/activityCtaPaths';
import {
  activityCenterActionButton,
  activityCenterSubLinkLabel,
  activityCenterTitle,
  activityUnreadDotClass,
} from '../../utils/activityCenterUtils';
import { activityCategoryShortLabel } from '../../utils/activityFeedFormat';
import { formatActivityRelativeTime } from '../../utils/activityFeedFormat';
import {
  ActivityFeedIconCalendar,
  ActivityFeedIconClock,
  ActivityFeedIconEye,
  ActivityFeedIconFile,
  ActivityFeedIconHeart,
  ActivityFeedIconMessage,
  ActivityFeedIconSparkles,
} from './activityFeedIcons';

type Visual = {
  Icon: ComponentType<{ className?: string }>;
  well: string;
};

function activityVisual(category: string): Visual {
  switch (category) {
    case 'recruiter_viewed_profile':
      return {
        Icon: ActivityFeedIconEye,
        well: 'ac-icon-well ac-icon-well--green',
      };
    case 'recruiter_liked_section':
      return {
        Icon: ActivityFeedIconHeart,
        well: 'ac-icon-well ac-icon-well--rose',
      };
    case 'recruiter_message_received':
    case 'recruiter_chat_request':
      return {
        Icon: ActivityFeedIconMessage,
        well: 'ac-icon-well ac-icon-well--blue',
      };
    case 'user_reference_added':
    case 'user_reference_confirmed':
      return {
        Icon: ActivityFeedIconFile,
        well: 'ac-icon-well ac-icon-well--teal',
      };
    case 'recruiter_meeting_request':
    case 'recruiter_meeting_approved':
      return {
        Icon: ActivityFeedIconCalendar,
        well: 'ac-icon-well ac-icon-well--indigo',
      };
    case 'recruiter_profile_dwell':
      return {
        Icon: ActivityFeedIconClock,
        well: 'ac-icon-well ac-icon-well--amber',
      };
    default:
      return {
        Icon: ActivityFeedIconSparkles,
        well: 'ac-icon-well ac-icon-well--slate',
      };
  }
}

export function ActivityCenterRow({
  event,
  unread,
  highlighted,
  isLast,
}: {
  event: ActivityEvent;
  unread: boolean;
  highlighted?: boolean;
  isLast?: boolean;
}) {
  const { Icon, well } = activityVisual(event.category);
  const title = activityCenterTitle(event.category);
  const shortKind = activityCategoryShortLabel(event.category);
  const subLink = activityCenterSubLinkLabel(event.category);
  const action = activityCenterActionButton(event.category);
  const ctaHref = resolveActivityCtaPath(
    event.category,
    event.cta_path,
    event.cta_label,
    event.metadata
  );
  const tsRelative = formatActivityRelativeTime(event.created_at);

  return (
    <li
      id={`activity-event-${event.id}`}
      className={`ac-row ${highlighted ? 'ac-row--highlighted' : ''} ${unread ? 'ac-row--unread' : ''}`}
    >
      <div className="ac-row-timeline">
        <div className={`ac-row-icon ${well}`}>
          <Icon className="ac-row-icon-svg" />
        </div>
        {!isLast ? <span className="ac-row-line" aria-hidden /> : null}
      </div>

      <div className="ac-row-body">
        <div className="ac-row-main">
          <div className="ac-row-copy">
            <div className="ac-row-title-row">
              {unread ? (
                <span className={activityUnreadDotClass(event.category)} aria-hidden />
              ) : null}
              <h3 className="ac-row-title">{title}</h3>
            </div>
            <p className="ac-row-desc">{event.message}</p>
            <p className="ac-row-meta">
              <span>{shortKind}</span>
              <span className="ac-row-meta-sep" aria-hidden>
                ·
              </span>
              <Link to={ctaHref} className="ac-row-meta-link">
                {subLink}
              </Link>
            </p>
          </div>
          <div className="ac-row-side">
            {tsRelative ? (
              <time className="ac-row-time" dateTime={event.created_at}>
                {tsRelative}
              </time>
            ) : null}
            <Link
              to={ctaHref}
              className={action.primary ? 'ac-row-btn ac-row-btn--primary' : 'ac-row-btn'}
            >
              {action.label}
            </Link>
          </div>
        </div>
      </div>
    </li>
  );
}
